// Попап поверх игры: доска с заголовком, страница на ней и листалка вокруг.
//
// Попапов в игре два: таблица выплат и правила, см. ui/paytable.ts и ui/rules.ts. Оба они
// устроены одинаково — стопка страниц, которую игрок листает стрелками, — поэтому доска,
// заголовок, крест и листалка живут здесь, а страницы приходят готовыми.
//
// Страница — это обычный узел. Попап их не строит и внутрь не заглядывает: он только показывает
// одну из них и прячет остальные. Страницы остаются на месте при смене языка: она меняет
// только их надписи.
//
// Пока попап открыт, затемнение забирает нажатия себе, а пробел не доходит до кнопки спина:
// клавиша перехватывается на погружении, как это делает кнопка на створке, см. ui/plateButton.ts.
// Закрывают попап крест, Escape и пробел, листают — стрелки на экране и стрелки на клавиатуре.
//
// Доску художник нарисовал под покупку фичи, и поперёк неё идут два завитка. Страницу текста
// они перечёркивают, поэтому игра собирает доску из трёх кусков картинки и берёт только то,
// что между ними, см. POPUP_BOARD.

import { Container, Graphics, Rectangle, Sprite, Text, Texture, type TextStyleOptions } from 'pixi.js';

import { DIVIDER_IMAGE, MISC_UI_SHEET, PAGER_SHEET, POPUP_BOARD, POPUP_BOARD_IMAGE, POPUP_TITLE_IMAGE, TITLE_FONT, type MiscUiFrame, type PagerFrame } from '../assets/catalog';
import type { AssetManager, TextureSheet } from '../assets/manager';
import { textFamily } from '../i18n/text';

import { RoundButton, SpriteButton } from './button';
import { fitLabel } from './label';
import type { Viewport } from './scene';

/** Насколько затемнение гасит игру под собой. Оно то же, что у сообщения об ошибке связи. */
const DIM_ALPHA: number = 0.75;

/** Высота доски. Ширину задаёт картинка, а высоту игра берёт сама: середина доски тянется. */
const BOARD_HEIGHT: number = 860;

/** Место заголовка: плашка ложится на верх доски и накрывает кольца блокнота. */
const TITLE_SCALE: number = 0.72;
const TITLE_Y: number = -BOARD_HEIGHT / 2 + 30;

/**
 * Ширина синего поля на плашке заголовка, в которое встаёт надпись.
 *
 * Плашка нарисована 863x250, и медная рамка занимает на ней края: синее поле идёт от 121 до 742.
 * Надпись длиннее него ложилась бы прямо на рамку, а «PAYTABLE» по-итальянски вдвое длиннее
 * английского, см. ui/label.ts.
 */
export const TITLE_WIDTH: number = 621 * TITLE_SCALE;

/** Середина креста, которым попап закрывают. Он лежит на правом верхнем углу доски. */
const CLOSE_X: number = 415;
const CLOSE_Y: number = -400;

/** Ширина страницы. Уже доски: по краям идёт рамка с угловыми завитками. */
export const PAGE_WIDTH: number = 780;

/** Верх и низ страницы. Верх стоит под плашкой заголовка, низ — над нижними завитками доски. */
export const PAGE_TOP: number = -290;
export const PAGE_BOTTOM: number = 310;

/** Насколько стрелка листалки отходит от края доски. */
const ARROW_X: number = POPUP_BOARD.width / 2 + 80;

/** Точки страниц идут под доской: на самой доске места им уже нет. */
const DOTS_Y: number = BOARD_HEIGHT / 2 + 40;
const DOT_SCALE: number = 0.55;
const DOT_STEP: number = 46;

/** Размеры надписей попапа. */
const TITLE_SIZE: number = 44;
const HEADING_SIZE: number = 34;
const TEXT_SIZE: number = 24;

/** Надпись на плашке заголовка. */
function titleStyle(family: string): TextStyleOptions {
  return {
    fontFamily: textFamily(family),
    fontSize: TITLE_SIZE,
    fontWeight: 'bold',
    fill: 0xf3dcae,
    letterSpacing: 4,
    align: 'center',
  };
}

/** Заголовок страницы: он крупнее её текста и другого цвета. */
export function headingStyle(family: string): TextStyleOptions {
  return {
    fontFamily: textFamily(family),
    fontSize: HEADING_SIZE,
    fontWeight: 'bold',
    fill: 0xf0c98a,
    align: 'center',
  };
}

/**
 * Текст страницы. Он переносится по словам сам: длина строки перевода игре неизвестна.
 *
 * Рвать слова тоже приходится: тайский и китайский пишут без пробелов, и целый абзац идёт
 * на них одним словом. Перенос по пробелам такую строку не тронул бы, и она ушла бы за края
 * доски. Слова латиницы это не задевает: рвётся только то, что само по себе шире строки.
 */
export function textStyle(family: string): TextStyleOptions {
  return {
    fontFamily: textFamily(family),
    fontSize: TEXT_SIZE,
    fill: 0xe8e2d4,
    align: 'center',
    wordWrap: true,
    wordWrapWidth: PAGE_WIDTH,
    breakWords: true,
    lineHeight: TEXT_SIZE * 1.35,
  };
}

/** Где на странице стоит заголовок и завиток под ним. Завиток высокий, отсюда и промежуток. */
const HEADING_Y: number = PAGE_TOP + 22;
const DIVIDER_Y: number = PAGE_TOP + 85;
const DIVIDER_SCALE: number = 0.5;

/** Где начинается содержимое страницы под завитком. */
export const CONTENT_Y: number = PAGE_TOP + 125;

/** Низ надписи под содержимым страницы: ниже идёт уже рамка доски. */
const NOTE_BOTTOM: number = PAGE_BOTTOM - 10;

/** Промежуток между содержимым страницы и надписью под ним. */
export const NOTE_GAP: number = 20;

/**
 * Выше какого края надпись внизу страницы не поднимается.
 *
 * Надпись растёт вверх от низа страницы, поэтому длинный перевод отодвигает иллюстрацию,
 * а не ложится на неё. Половину места под содержимым надпись всё же не переходит: перевод
 * бывает вдвое длиннее английского, и иллюстрация ужалась бы в полоску.
 */
const NOTE_TOP: number = (CONTENT_Y + NOTE_BOTTOM) / 2;

/**
 * Заголовок страницы с медным завитком под ним.
 *
 * Завиток нарисован отдельной картинкой: те, что художник врисовал в саму доску, игра
 * не показывает, см. POPUP_BOARD.
 */
export function pageHeading(caption: string, family: string, assets: AssetManager): Container {
  const view = new Container();

  const line = new Text({ text: caption, style: headingStyle(family) });
  line.anchor.set(0.5);
  line.position.set(0, HEADING_Y);
  fitLabel(line, { width: PAGE_WIDTH });

  const curl = new Sprite(assets.texture(DIVIDER_IMAGE));
  curl.anchor.set(0.5);
  curl.scale.set(DIVIDER_SCALE);
  curl.position.set(0, DIVIDER_Y);

  view.addChild(line, curl);

  return view;
}

/**
 * Иллюстрация посреди страницы.
 *
 * Картинки нарисованы разного размера, от 431x388 до 885x190, а места под них на странице
 * поровну. Поэтому берётся меньшее из двух отношений: картинка встаёт целиком и без искажения.
 * Крупнее нарисованного она не растёт: мелкая картинка во всю страницу расплывётся.
 *
 * @param bottom Низ отведённого картинке места. Его задаёт надпись под ней: чем длиннее
 * перевод, тем выше картинка встаёт.
 */
function pagePicture(texture: Texture, bottom: number): Sprite {
  const height: number = bottom - CONTENT_Y;

  const picture = new Sprite(texture);
  picture.anchor.set(0.5);
  picture.scale.set(Math.min(PAGE_WIDTH / texture.width, height / texture.height, 1));
  picture.position.set(0, (CONTENT_Y + bottom) / 2);

  return picture;
}

/**
 * Надпись у нижнего края страницы: под ней уже рамка доски.
 *
 * Растёт надпись вверх, поэтому длинный перевод занимает место у того, что стоит выше.
 * Выше своего края она не поднимается, а ужимается, см. ui/label.ts.
 *
 * @param top Выше какого края надпись не поднимается. Его задаёт то, что стоит на странице
 * над ней: иллюстрации хватает половины страницы, а сетке выплат мини-слота — нет.
 */
export function pageNote(caption: string, family: string, top: number = NOTE_TOP): Text {
  const note = new Text({ text: caption, style: textStyle(family) });
  note.anchor.set(0.5, 1);
  note.position.set(0, NOTE_BOTTOM);
  fitLabel(note, { width: PAGE_WIDTH, height: NOTE_BOTTOM - top });

  return note;
}

/**
 * Иллюстрация и надпись под ней: то, из чего собрана страница про механику игры.
 *
 * Место они делят снизу вверх. Сперва его занимает надпись — её длину задаёт перевод, и знает
 * её только сама надпись, — а иллюстрация берёт остаток. Считай игра наоборот, и длинный
 * перевод лёг бы поверх картинки.
 */
export function pageBody(texture: Texture, caption: string, family: string): Container {
  const view = new Container();
  const note: Text = pageNote(caption, family);

  view.addChild(pagePicture(texture, NOTE_BOTTOM - note.height - NOTE_GAP), note);

  return view;
}

/** Вырезает из картинки полосу во всю её ширину. */
function slice(texture: Texture, y: number, height: number): Texture {
  return new Texture({ source: texture.source, frame: new Rectangle(0, y, texture.width, height) });
}

/**
 * Собирает доску нужной высоты из трёх кусков картинки.
 *
 * Верх и низ идут как нарисованы, середина тянется. Стыкуются куски по ровной заливке,
 * поэтому шва на доске не видно.
 */
function board(texture: Texture): Container {
  const view = new Container();

  const head = new Sprite(slice(texture, 0, POPUP_BOARD.head));
  head.anchor.set(0.5, 0);
  head.position.set(0, -BOARD_HEIGHT / 2);

  const foot = new Sprite(slice(texture, POPUP_BOARD.foot.y, POPUP_BOARD.foot.height));
  foot.anchor.set(0.5, 1);
  foot.position.set(0, BOARD_HEIGHT / 2);

  const middle = new Sprite(slice(texture, POPUP_BOARD.stretch.y, POPUP_BOARD.stretch.height));
  middle.anchor.set(0.5, 0);
  middle.position.set(0, -BOARD_HEIGHT / 2 + POPUP_BOARD.head);
  middle.height = BOARD_HEIGHT - POPUP_BOARD.head - POPUP_BOARD.foot.height;

  view.addChild(middle, head, foot);

  return view;
}

/** Попап со страницами. */
export class Popup {
  readonly view: Container = new Container();

  readonly #dim: Graphics = new Graphics();
  readonly #pages: readonly Container[];
  readonly #caption: Text;

  readonly #close: RoundButton;
  readonly #back: SpriteButton;
  readonly #next: SpriteButton;
  readonly #dots: Sprite[] = [];

  readonly #dotOn: Texture;
  readonly #dotOff: Texture;

  /** Какая страница открыта. */
  #page = 0;

  /**
   * @param title Надпись на плашке заголовка.
   * @param pages Страницы попапа. Пустой стопки не бывает: листать было бы нечего.
   * @throws Error если страниц нет
   */
  constructor(assets: AssetManager, title: string, pages: readonly Container[]) {
    if (pages.length === 0)
      throw new Error('Попап без страниц: показывать нечего');

    const ui: TextureSheet<MiscUiFrame> = assets.sheet(MISC_UI_SHEET);
    const pager: TextureSheet<PagerFrame> = assets.sheet(PAGER_SHEET);
    const family: string = assets.font(TITLE_FONT);

    this.#pages = pages;
    this.#dotOn = pager.pageOn;
    this.#dotOff = pager.pageOff;

    // Затемнение забирает нажатия себе: пока попап на экране, игра их не получает.
    this.#dim.eventMode = 'static';

    const banner = new Sprite(assets.texture(POPUP_TITLE_IMAGE));
    banner.anchor.set(0.5);
    banner.scale.set(TITLE_SCALE);
    banner.position.set(0, TITLE_Y);

    this.#caption = new Text({ text: title, style: titleStyle(family) });
    this.#caption.anchor.set(0.5);
    this.#caption.position.set(0, TITLE_Y);
    fitLabel(this.#caption, { width: TITLE_WIDTH });

    this.#close = new RoundButton({ idle: ui.closeIdle, hover: ui.closeHover, active: ui.closeActive }, () => this.close());
    this.#close.view.position.set(CLOSE_X, CLOSE_Y);

    // Стрелка нарисована влево, поэтому правая — та же картинка, отражённая по горизонтали.
    const faces = { idle: pager.arrowIdle, hover: pager.arrowHover, active: pager.arrowActive, blocked: pager.arrowBlocked };

    this.#back = new SpriteButton(faces, () => this.#turn(-1));
    this.#back.view.position.set(-ARROW_X, 0);

    this.#next = new SpriteButton(faces, () => this.#turn(1));
    this.#next.view.position.set(ARROW_X, 0);
    this.#next.view.scale.x = -1;

    const dots = new Container();
    for (let index = 0; index < pages.length; index++) {
      const dot = new Sprite(this.#dotOff);
      dot.anchor.set(0.5);
      dot.scale.set(DOT_SCALE);
      dot.position.set((index - (pages.length - 1) / 2) * DOT_STEP, DOTS_Y);

      this.#dots.push(dot);
      dots.addChild(dot);
    }

    // Страницы стоят стопкой на одном месте: видна всегда ровно одна.
    const stack = new Container();
    for (const page of pages) {
      page.visible = false;
      stack.addChild(page);
    }

    this.view.visible = false;
    this.view.addChild(this.#dim, board(assets.texture(POPUP_BOARD_IMAGE)), stack, banner, this.#caption, this.#close.view, this.#back.view, this.#next.view, dots);

    // Листалку и первую страницу игра ставит сразу: попап открывается уже собранным.
    this.#show(0);
  }

  /** Открыт ли попап. */
  get open(): boolean {
    return this.view.visible;
  }

  /** Какая страница открыта, считая с нуля. */
  get page(): number {
    return this.#page;
  }

  /** Меняет заголовок, сохраняя открытое окно и выбранную страницу. */
  set title(value: string) {
    this.#caption.text = value;
    fitLabel(this.#caption, { width: TITLE_WIDTH });
  }

  /** Показывает попап с первой страницы и забирает себе клавиатуру. */
  show(): void {
    if (this.view.visible)
      return;

    this.view.visible = true;
    this.#show(0);

    window.addEventListener('keydown', this.#onKeyDown, true);
  }

  /** Убирает попап и отпускает клавиатуру. */
  close(): void {
    if (!this.view.visible)
      return;

    this.view.visible = false;

    window.removeEventListener('keydown', this.#onKeyDown, true);
  }

  /** Двигает затухание креста и стрелок. */
  update(seconds: number): void {
    this.#close.update(seconds);
    this.#back.update(seconds);
    this.#next.update(seconds);
  }

  /** Растягивает затемнение на всю видимую область: под попапом не должно остаться игры. */
  layout(viewport: Viewport): void {
    this.#dim.clear();
    this.#dim.rect(-viewport.width / 2, -viewport.height / 2, viewport.width, viewport.height);
    this.#dim.fill({ color: 0x000000, alpha: DIM_ALPHA });
  }

  /** Снимает клавиатуру. Зовётся, когда сцена уходит с экрана. */
  destroy(): void {
    window.removeEventListener('keydown', this.#onKeyDown, true);
  }

  /** Листает на заданное число страниц. За края стопки листалка не пускает. */
  #turn(step: number): void {
    const next: number = this.#page + step;

    if (next >= 0 && next < this.#pages.length)
      this.#show(next);
  }

  /** Показывает заданную страницу и приводит листалку к ней. */
  #show(page: number): void {
    this.#pages[this.#page].visible = false;
    this.#pages[page].visible = true;
    this.#page = page;

    this.#back.enabled = page > 0;
    this.#next.enabled = page < this.#pages.length - 1;

    for (let index = 0; index < this.#dots.length; index++)
      this.#dots[index].texture = index === page ? this.#dotOn : this.#dotOff;
  }

  /**
   * Ведёт попап с клавиатуры.
   *
   * Клавиша перехватывается на погружении и дальше не идёт: пока попап на экране, пробел
   * принадлежит ему, а не кнопке спина. Стрелки листают, Escape и пробел закрывают.
   */
  readonly #onKeyDown = (event: KeyboardEvent): void => {
    const keys: readonly string[] = ['Escape', 'Space', 'ArrowLeft', 'ArrowRight'];
    if (!keys.includes(event.code))
      return;

    event.preventDefault();
    event.stopPropagation();

    if (event.repeat)
      return;

    if (event.code === 'ArrowLeft')
      this.#turn(-1);
    else if (event.code === 'ArrowRight')
      this.#turn(1);
    else
      this.close();
  };
}
